import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

/** Um registro do histórico do processo. */
export interface ProcessoEvento {
  id: number;
  prePedidoId?: number | null;
  prePedidoNumero?: number | null;
  pedidoId?: number | null;
  pedidoNumero?: number | null;
  usuarioId?: number | null;
  usuarioNome?: string | null;
  dataEvento: string;
  acao: string;
  statusAnteriorId?: number | null;
  statusAnteriorNome?: string | null;
  statusNovoId?: number | null;
  statusNovoNome?: string | null;
  entidade?: string | null;
  campo?: string | null;
  valorAnterior?: string | null;
  valorNovo?: string | null;
  descricao?: string | null;
}

/**
 * Uma etapa do filtro. Vem da união de `pre_pedido_status` e `pedido_status`.
 *
 * A `origem` viaja junto porque **os ids das duas tabelas colidem**: 1 é
 * "Em Elaboração" num vocabulário e "Ag. Separação" no outro. É ela que o servidor
 * usa para saber por qual campo filtrar.
 */
export interface ProcessoEtapa {
  id: number;
  nome: string;
  ordem: number;
  origem: 'pre_pedido' | 'pedido' | string;
}

export interface ProcessoHistoricoFiltros {
  dataInicio?: string | null;
  dataFim?: string | null;
  usuarioId?: number | null;
  acao?: string | null;
  statusId?: number | null;
  etapaOrigem?: string | null;
  ordenarPor?: string | null;
  descendente?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ProcessoHistoricoAPIService {
  private baseUrl = `${environment.APIEndpoint}processo-historico`;

  constructor(private http: HttpClient) { }

  /**
   * Histórico de um pré-pedido, ou de um pedido.
   *
   * Informar o pedido traz também o período em que ele ainda era pré-pedido — a
   * criação do pedido não encerra a rastreabilidade.
   */
  listar(
    origem: { prePedidoId?: number | null; pedidoId?: number | null },
    filtros: ProcessoHistoricoFiltros,
    pageNumber: number,
    pageSize: number
  ): Observable<any> {
    const rota = origem.pedidoId ? `pedido/${origem.pedidoId}` : `pre-pedido/${origem.prePedidoId}`;
    const url = `${this.baseUrl}/${rota}?${this.montarQuery(filtros)}&pageNumber=${pageNumber}&pageSize=${pageSize}`;

    return this.http.get<any>(url);
  }

  /**
   * Exporta o histórico com os filtros aplicados — o recorte inteiro, não só a
   * página aberta. `blob` porque a resposta é uma planilha.
   */
  exportar(
    origem: { prePedidoId?: number | null; pedidoId?: number | null },
    filtros: ProcessoHistoricoFiltros
  ): Observable<Blob> {
    const chaveOrigem = origem.pedidoId
      ? `pedidoId=${origem.pedidoId}`
      : `prePedidoId=${origem.prePedidoId}`;

    const url = `${this.baseUrl}/exportar?${chaveOrigem}&${this.montarQuery(filtros)}`;

    return this.http.get(url, { responseType: 'blob' });
  }

  /** Etapas oferecidas no filtro: as do pré-pedido e as do pedido, numa lista só. */
  listarEtapas(): Observable<ProcessoEtapa[]> {
    return this.http.get<ProcessoEtapa[]>(`${this.baseUrl}/etapas`);
  }

  /**
   * Query string montada por concatenação com `if` por parâmetro, no padrão dos
   * demais serviços do projeto — parâmetro nulo não vira `?x=null`.
   */
  private montarQuery(f: ProcessoHistoricoFiltros): string {
    const partes: string[] = [];

    if (f.dataInicio)  partes.push(`dataInicio=${f.dataInicio}`);
    if (f.dataFim)     partes.push(`dataFim=${f.dataFim}`);
    if (f.usuarioId)   partes.push(`usuarioId=${f.usuarioId}`);
    if (f.acao)        partes.push(`acao=${encodeURIComponent(f.acao)}`);
    if (f.statusId)    partes.push(`statusId=${f.statusId}`);
    // A origem só faz sentido junto do id, e sem ela o servidor assume pré-pedido.
    if (f.statusId && f.etapaOrigem) partes.push(`etapaOrigem=${encodeURIComponent(f.etapaOrigem)}`);
    if (f.ordenarPor)  partes.push(`ordenarPor=${encodeURIComponent(f.ordenarPor)}`);

    partes.push(`descendente=${f.descendente !== false}`);

    return partes.join('&');
  }
}
